import { useState } from 'react';
import { constants } from '@helpers/constants';
import { erpService } from '@services/erp';
import { useDispatch, useSelector } from '@services/hooks/redux-hooks';
import useRefState from '@services/hooks/useRefState';
import { getCosteEmpleado } from '@services/hooks/UserHook';
import { reportLinesService } from '@services/reportLines';
import { updateLine } from '@store/slices/lines';

const NO_MATCH_CATEGORY_TYPEHOUR = 'no-match-category-typeHour';

interface UseSalesPricingReturn {
  refreshSalesPricingData: (index: number) => Promise<void>;
  setExternalSelectedObra: (obra: any) => void;
}

const useSalesPricing = (): UseSalesPricingReturn => {
  const dispatch = useDispatch();

  const selectedobra = useSelector((state: any) => state.selectedobra.obra);
  const manoObrasList = useSelector((state: any) => state.mano_obra.list);
  const empresas = useSelector((state: any) => state.empresas.list);

  const [, setLoadingSP] = useState<boolean>(false);
  const [externalSelectedObra, setExternalSelectedObra] = useState<any>(null);

  // Configuración almacenada en localStorage
  const config = localStorage?.getItem('ef-configuration') || '';
  const config_JSON = JSON.parse(config || '{}');

  // Calendarios cuando tipo de hora es "varios".
  const [calendariosEnTipoDeHoraVarios] = useRefState<any[]>([]);

  const isEqual = (a: unknown, b: unknown) =>
    JSON.stringify(a) === JSON.stringify(b);

  const changeLineHandler = (line: any, changes: any) => {
    if (!isEqual(line, changes)) {
      dispatch(updateLine(changes));
    }
  };

  const getHoursDecimal = (line: any) => {
    const date = selectedobra?.date ? new Date(selectedobra.date) : new Date();
    const lineParsed = reportLinesService.parseReportLine(line, date);

    return lineParsed?.horas ? Number(lineParsed.horas) : 0;
  };

  const getArticleManoObra = (
    categoriaValue: any,
    categoriaExternoValue: any,
    typeHourValue: any,
  ) => {
    let articleValue: any = null;
    let articleExternoValue: any = null;

    const getting = (categoria: any, tipoHora: any, company: any) => {
      let article: any = null;

      if (categoria && tipoHora) {
        const empresaSelected = empresas.find(
          (emp: any) => emp.Empresa == company,
        );

        const manoObraSelected = manoObrasList.find(
          (c: any) =>
            c.Categoria == categoria &&
            c.Tipo_Hora == tipoHora &&
            c.Ambito == empresaSelected.Ambito,
        );

        if (!manoObraSelected) {
          article = NO_MATCH_CATEGORY_TYPEHOUR;
        } else {
          article = manoObraSelected.Articulo;
        }
      }

      return article;
    };

    if (
      categoriaValue &&
      typeHourValue &&
      manoObrasList &&
      manoObrasList.length > 0
    ) {
      if (typeHourValue == constants.tipo_hora_varios.TipoHora) {
        const dataGetting = calendariosEnTipoDeHoraVarios.map((calendario) => {
          const article = getting(
            categoriaValue,
            calendario.TipoHora,
            selectedobra.company,
          );
          const articleExterno = getting(
            categoriaExternoValue,
            calendario.TipoHora,
            selectedobra.companyDestino,
          );

          return {
            calendario,
            article,
            articleExterno,
          };
        });

        articleValue = dataGetting.map((result) => ({
          calendario: result.calendario,
          article: result.article,
        }));

        articleExternoValue = dataGetting.map((result) => ({
          calendario: result.calendario,
          article: result.articleExterno,
        }));
      } else {
        articleValue = getting(
          categoriaValue,
          typeHourValue,
          selectedobra.company,
        );
        articleExternoValue = getting(
          categoriaExternoValue,
          typeHourValue,
          selectedobra.companyDestino,
        );
      }
    }

    return {
      article: articleValue,
      articleExterno: articleExternoValue,
    };
  };

  const getPrecioManoObra = async (
    line: any,
    categoriaValue: any,
    categoriaExternoValue: any,
    typeHourValue: any,
    costeEmpleado: any,
    quantity: any,
    externalProject?: boolean,
  ) => {
    let precioValue: any = null;
    let precioExternoValue: any = null;
    let salesValue: any = null;
    let salesExternoValue: any = null;

    const fetching = async (
      categoria: any,
      tipoHora: any,
      coste: any,
      qty: any,
      company: any,
      isExternal?: boolean,
    ) => {
      let precio: any = null;
      let sales: any = null;

      if (categoria && tipoHora) {
        const empresaSelected = empresas.find(
          (emp: any) => emp.Empresa == company,
        );

        const manoObraSelected = manoObrasList.find(
          (c: any) =>
            c.Categoria == categoria &&
            c.Tipo_Hora == tipoHora &&
            c.Ambito == empresaSelected.Ambito,
        );

        if (!manoObraSelected) {
          precio = NO_MATCH_CATEGORY_TYPEHOUR;
          sales = NO_MATCH_CATEGORY_TYPEHOUR;
        } else {
          setLoadingSP(true);

          try {
            const salesResponse = await erpService.getSalesPricing(
              manoObraSelected,
              isExternal ? externalSelectedObra : selectedobra,
              qty,
              line,
            );

            const salesPricing = salesResponse?.data?.data?.data_sales_pricing;

            precio = coste;
            sales = salesPricing;
          } catch (error) {
            precio = 0;
            sales = {};
          }

          setLoadingSP(false);
        }
      }

      return { precioValue: precio, salesValue: sales };
    };

    if (
      (externalProject || selectedobra.Administracion) &&
      categoriaValue &&
      typeHourValue &&
      manoObrasList &&
      manoObrasList.length > 0 &&
      config_JSON?.ftp_method == 'endpoint'
    ) {
      if (typeHourValue == constants.tipo_hora_varios.TipoHora) {
        const dataFetchingPromises = calendariosEnTipoDeHoraVarios.map(
          async (calendario) => {
            const dataFetching = await fetching(
              categoriaValue,
              calendario.TipoHora,
              costeEmpleado,
              quantity,
              selectedobra.company,
            );

            const dataFetchingExterno = await fetching(
              categoriaExternoValue,
              calendario.TipoHora,
              costeEmpleado,
              quantity,
              selectedobra.companyDestino,
              externalProject,
            );

            const precio = dataFetching.precioValue;
            const precioExterno = dataFetchingExterno.precioValue;
            const sales = dataFetching.salesValue;
            const salesExterno = dataFetchingExterno.salesValue;

            return { calendario, precio, precioExterno, sales, salesExterno };
          },
        );

        const allResults = await Promise.all(dataFetchingPromises);

        precioValue = allResults.map((result) => ({
          calendario: result.calendario,
          precio: result.precio,
        }));

        precioExternoValue = allResults.map((result) => ({
          calendario: result.calendario,
          precio: result.precioExterno,
        }));

        salesValue = allResults.map((result) => ({
          calendario: result.calendario,
          sales: result.sales,
        }));

        salesExternoValue = allResults.map((result) => ({
          calendario: result.calendario,
          sales: result.salesExterno,
        }));
      } else {
        const [dataFetching, dataFetchingExterno] = await Promise.all([
          fetching(
            categoriaValue,
            typeHourValue,
            costeEmpleado,
            quantity,
            selectedobra.company,
          ),
          fetching(
            categoriaExternoValue,
            typeHourValue,
            costeEmpleado,
            quantity,
            selectedobra.companyDestino,
            externalProject,
          ),
        ]);

        precioValue = dataFetching.precioValue;
        precioExternoValue = dataFetchingExterno.precioValue;
        salesValue = dataFetching.salesValue;
        salesExternoValue = dataFetchingExterno.salesValue;
      }
    } else if (!selectedobra.Administracion) {
      precioValue = costeEmpleado ?? 0;
      precioExternoValue = costeEmpleado ?? 0;
      salesValue = null;
      salesExternoValue = null;
    }

    return { precioValue, precioExternoValue, salesValue, salesExternoValue };
  };

  const refreshSalesPricingData = async (line: any) => {
    if(line.type != 'manoDeObra') return { ...line };
    
    const currentLine = { ...line };

    const categoriaValue = currentLine?.category ?? null;
    const categoriaExternoValue = currentLine?.categoryExterno ?? null;
    const typeHourValue = currentLine?.typeHour ?? null;
    const costeEmpleadoValue =
      currentLine?.costeEmpleado ??
      currentLine?.userWorker?.data?.coste ??
      getCosteEmpleado();

    const horasDecimal = getHoursDecimal(currentLine);

    const optionalParams: any = {};

    if (selectedobra.Administracion || externalSelectedObra?.Administracion) {
      const { precioValue, precioExternoValue, salesValue, salesExternoValue } =
        await getPrecioManoObra(
          line,
          categoriaValue,
          categoriaExternoValue,
          typeHourValue,
          costeEmpleadoValue,
          horasDecimal,
          externalSelectedObra?.Administracion,
        );

      currentLine.priceWithErrors = !Boolean(salesValue?.price);

      if (selectedobra.Administracion) {
        Object.assign(optionalParams, {
          precio: precioValue,
          precioExterno: precioExternoValue,
          data_sales_pricing: salesValue,
          data_sales_pricing_externo: salesExternoValue,
        });
      }

      if (externalSelectedObra?.Administracion) {
        Object.assign(optionalParams, {
          precioExterno: precioExternoValue,
          data_sales_pricing_externo: salesExternoValue,
        });
      }
    }

    if (currentLine?.type === 'manoDeObra') {
      const { article, articleExterno } = getArticleManoObra(
        categoriaValue,
        categoriaExternoValue,
        typeHourValue,
      );

      currentLine.article = article;
      currentLine.articleExterno = articleExterno;
    }

    const updatedLine = {
      ...currentLine,
      ...optionalParams,
      user: currentLine?.userWorker?.id,
    };

    changeLineHandler(line, updatedLine);

    return updatedLine;
  };

  return {
    refreshSalesPricingData,
    setExternalSelectedObra,
  };
};

export { useSalesPricing };
